import { Link } from '@mui/joy';
import { ICellRendererParams } from 'ag-grid-community';
import { t } from 'i18next';
import { useCallback, useState } from 'react';

import { DEFAULT_PLACEHOLDER } from 'core/constants';
import { Tooltip } from 'core/elements';
import { Level } from 'theme/enums';

import { ImportDetailsPanel } from '../ImportDetailsPanel';

export const ImportIdCell = ({ data, value, valueFormatted }: ICellRendererParams) => {
  // Molecular: the cell owns the panel it opens rather than threading state up
  // through the table and page.
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);

  const onOpenDetails = useCallback(() => setIsDetailsOpen(true), []);
  const onCloseDetails = useCallback(() => setIsDetailsOpen(false), []);

  // Group rows have no data and keep the plain (possibly empty) label.
  if (!data) {
    return <>{valueFormatted ?? value}</>;
  }

  // component="button" puts the link in the tab order — a Joy Link with an
  // onClick and no href renders an <a> that no keyboard can reach.
  const link = (
    <Link component="button" level={Level.BODY_SM} onClick={onOpenDetails}>
      {value ?? DEFAULT_PLACEHOLDER}
    </Link>
  );

  return (
    <>
      {/* The placeholder stays clickable when there is no import id: the panel
          looks its trace up by document id and correlation guid, not by import
          id, so an upload that died before an import record was written — the
          row that most needs its raw log — still has one to open. Only that case
          gets the tooltip, since only it needs explaining. */}
      {value ? link : <Tooltip title={t('tooltipOpenImportDetails')}>{link}</Tooltip>}

      {/* Mounted only while open: this cell renders once per visible row, and an
          always-mounted panel would put a drawer in the DOM for every one. */}
      {isDetailsOpen && (
        <ImportDetailsPanel attempt={data} isOpen={isDetailsOpen} onClose={onCloseDetails} />
      )}
    </>
  );
};
